// src/features/imports/components/ImportReviewPanel.jsx
//
// INGEST-WIZARD-UX-3 — Shared rich review panel (bulk actions, side-by-side
// diffs, provenance, Load more).
//
// Used in two contexts:
//   1. Inside a Modal via ImportSessionReview (Pending-Imports resume path)
//   2. Inline inside the ImportWizard step 4 (wizard-forward path)
//
// Props:
//   sessionId       number
//   onBack          () => void
//   onRestage       () => void   (optional — wizard path)
//   onTitleChange   (title, subTitle) => void  (optional — ImportSessionReview uses this)

import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { OUTBOUND_LINK_STYLE } from "../../../v2/pulse/pulseConstants.js";
import { showToast } from "../../../components/toastService.jsx";
import { T } from "../../../app/tokens.js";
import { APP_NAME } from "../../../constants/branding.js";
import { Modal } from "../../../components/Modal.jsx";
import { DiffRow, DIFF_GRID_COLS } from "./DiffRow.jsx";
import { PdfExtractionPanel } from "./PdfExtractionPanel.jsx";
import { DocumentSourcesPanel } from "./DocumentSourcesPanel.jsx";
import {
  getImportSession,
  applyImportSession,
  rejectImportSession,
  decideImportDiff,
  bulkDecideImportDiffs,
  discardImportSession,
} from "../../../data/importSessionsClient.js";
import {
  getStagedRows,
  deleteStagedRow,
  getBadgingReview,
  getHeadcountReview,
} from "../../../data/importPipelineClient.js";
import { log } from "../../../utils/logger.js";
import { ErrorBanner } from "../../../components/ErrorBanner.jsx";

// ─── HeadcountReviewSummary ──────────────────────────────────────────────────
// INGEST-HEADCOUNT-TYPE-S1: Step 4 review for headcount sessions. Like
// badging, these never populate import_diffs — instead we show per-office
// ft/vc/ic breakdown (new vs. updated) sourced from
// GET /sessions/:id/headcount-review, plus Work Location resolution and
// remote/distributed aggregate counts.

function HeadcountReviewSummary({
  review, loading, error, busy, applyError,
  onApply, onReject, confirmDialog, onConfirmReject, onCancelConfirm,
  isReadOnly, onBack,
}) {
  if (loading && !review) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
        Loading headcount review…
      </div>
    );
  }

  if (error && !review) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.errorText, fontSize: T.fsBase }}>
        {error?.message || String(error)}
      </div>
    );
  }

  if (!review) return null;

  const {
    reviewCopy,
    locations,
    totals,
    unmappedRowCount,
    heldRowCount,
    heldUnmappedNameCount,
    heldAmbiguousFallbackCount,
    fallbackRowCount,
    vanishedNames,
    unmappedNames = [],
    resolvedNameCount = 0,
    inOfficeRowCount = 0,
    remoteDistributedRowCount = 0,
    totalRowCount = 0,
    asOfDate,
  } = review;

  return (
    <div data-testid="headcount-review-summary" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        flexShrink: 0, padding: "14px 18px", background: T.bg,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
          {reviewCopy?.title || "Headcount import"}
        </div>
        {reviewCopy?.description && (
          <div style={{ fontSize: T.fsXs5, color: T.mid, marginBottom: 10 }}>
            {reviewCopy.description}
          </div>
        )}
        {asOfDate && (
          <div style={{ fontSize: T.fsXs5, color: T.mid, marginBottom: 10 }}>
            Snapshot date: <strong>{asOfDate}</strong>
          </div>
        )}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: T.fsXs5 }}>
          <span data-testid="headcount-total-new" style={{ color: T.utilGreenText, fontWeight: T.fwSemibold }}>
            {totals.new.toLocaleString()} new
          </span>
          <span data-testid="headcount-total-updated" style={{ color: T.blue, fontWeight: T.fwSemibold }}>
            {totals.updated.toLocaleString()} updated
          </span>
          <span data-testid="headcount-in-office" style={{ color: T.dark, fontWeight: T.fwSemibold }}>
            {inOfficeRowCount.toLocaleString()} in-office
          </span>
          <span data-testid="headcount-remote-distributed" style={{ color: T.mid, fontWeight: T.fwSemibold }}>
            {remoteDistributedRowCount.toLocaleString()} remote/distributed
          </span>
          <span data-testid="headcount-total-rows" style={{ color: T.dark, fontWeight: T.fwSemibold }}>
            {totalRowCount.toLocaleString()} total
          </span>
          <span data-testid="headcount-resolved-by-name" style={{ color: T.utilGreenText, fontWeight: T.fwSemibold }}>
            {resolvedNameCount.toLocaleString()} resolved by name
          </span>
          <span data-testid="headcount-unmapped-names" style={{ color: unmappedNames.length ? T.warnAmberText : T.utilGreenText, fontWeight: T.fwSemibold }}>
            {unmappedNames.length.toLocaleString()} unmapped Work Location names
          </span>
          {unmappedRowCount > 0 && (
            <span data-testid="headcount-total-unmapped" style={{ color: T.warnAmberText, fontWeight: T.fwSemibold }}>
              {unmappedRowCount.toLocaleString()} remote rows excluded from office upserts
            </span>
          )}
          {heldRowCount > 0 && (
            <span data-testid="headcount-total-held" style={{ color: T.warnAmberText, fontWeight: T.fwSemibold }}>
              {heldRowCount.toLocaleString()} held (unmapped Work Location)
            </span>
          )}
          {fallbackRowCount > 0 && (
            <span data-testid="headcount-total-fallback" style={{ color: T.mid, fontWeight: T.fwSemibold }}>
              {fallbackRowCount.toLocaleString()} legacy fallback
            </span>
          )}
        </div>
        {(heldRowCount > 0 || vanishedNames?.length > 0) && (
          <div
            data-testid="headcount-drift-warning"
            style={{
              marginTop: 10,
              padding: "8px 10px",
              border: `1px solid ${T.utilAmberBorder}`,
              borderRadius: 5,
              background: T.warnAmberBg,
              color: T.warnAmberText,
              fontSize: T.fsXs5,
            }}
          >
            {heldRowCount > 0 && (
              <div>
                {heldRowCount} aggregated row{heldRowCount === 1 ? "" : "s"} held and excluded from office totals
                {heldUnmappedNameCount ? ` across ${heldUnmappedNameCount} unmapped name${heldUnmappedNameCount === 1 ? "" : "s"}` : ""}.
                {heldAmbiguousFallbackCount ? ` ${heldAmbiguousFallbackCount} had multiple live legacy IDs and require review.` : ""}
              </div>
            )}
            {vanishedNames?.length > 0 && (
              <div>
                {vanishedNames.length} confirmed Work Location name{vanishedNames.length === 1 ? "" : "s"} vanished from this export; no zero rows were fabricated.
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "10px 18px" }}>
        {locations.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
            No headcount rows staged for this session.
          </div>
        )}
        {locations.map((loc) => (
          <div
            key={loc.locationId}
            data-testid={`headcount-location-row-${loc.locationId}`}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 8,
              padding: "10px 14px", background: T.white,
            }}
          >
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
              {loc.marketName ? `${loc.marketName} · ${loc.locationName}` : loc.locationName}
            </span>
            <span style={{ display: "flex", gap: 14, fontSize: T.fsXs5, color: T.mid, alignItems: "center" }}>
              <span style={{ color: T.dark }}>
                FT {loc.ft ?? 0} · VC {loc.vc ?? 0} · IC {loc.ic ?? 0}
              </span>
              <span style={{
                fontWeight: T.fwSemibold,
                color: loc.status === "new" ? T.utilGreenText : T.blue,
              }}>
                {loc.status === "new" ? "New" : "Updated"}
              </span>
            </span>
          </div>
        ))}
      </div>

      {!isReadOnly && (
        <div style={{ flexShrink: 0 }}>
          {applyError && (
            <div
              role="alert"
              data-testid="headcount-apply-error"
              style={{
                margin: "0 18px", marginTop: 12, padding: "10px 14px",
                background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 6,
                color: T.dark, fontSize: T.fsSm,
              }}
            >
              Apply failed: {applyError?.message || String(applyError)}. Nothing was written - you can try again.
            </div>
          )}
          <div style={{
            padding: "12px 18px", borderTop: `1px solid ${T.border}`,
            display: "flex", justifyContent: "flex-end", gap: 10,
          }}>
            <button
              type="button"
              data-testid="headcount-reject-btn"
              onClick={onReject}
              disabled={busy}
              style={{
                padding: "9px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.border}`, background: T.white, color: T.mid,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              Reject
            </button>
            <button
              type="button"
              data-testid="headcount-apply-btn"
              onClick={onApply}
              disabled={busy}
              style={{
                padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: "none", background: T.blue, color: T.white,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "Applying…" : "Apply to headcount"}
            </button>
          </div>
        </div>
      )}

      {isReadOnly && (
        <div style={{
          flexShrink: 0, padding: "12px 18px", borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "flex-end",
        }}>
          <button
            type="button"
            data-testid="headcount-done-btn"
            onClick={onBack}
            style={{
              padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              border: "none", background: T.blue, color: T.white, cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog?.type === "reject"}
        title="Reject entire session?"
        message="This will discard all staged headcount rows for this session. This cannot be undone."
        confirmLabel="Reject session"
        onConfirm={onConfirmReject}
        onCancel={onCancelConfirm}
      />
    </div>
  );
}

// ─── Source type labels ───────────────────────────────────────────────────────

const SOURCE_LABELS = {
  spacebase_main_leases:      "Spacebase - Main Leases",
  spacebase_critical_dates:   "Spacebase - Critical Dates",
  spacebase_recurring_leases: "Spacebase - Recurring Leases",
  spacebase_contacts:         "Spacebase - Contacts",
  spacebase_documents:        "Spacebase - Documents",
  pdf_abstraction:            "PDF Abstraction",
  manual_csv:                 "Manual CSV",
};

const STATUS_LABELS = {
  staged:            "Staged",
  in_review:         "In review",
  applied:           "Applied",
  partially_applied: "Partially applied",
  rejected:          "Rejected",
};

const CHANGED_DIFF_TYPES = new Set(["value_change", "conflict", "provenance_promotion"]);

function isUnchangedDiff(diff) {
  if (diff.fieldName === "year_costs" || diff.scheduleWarnings?.length) return false;
  if (!CHANGED_DIFF_TYPES.has(diff.diffType)) return true;
  const cur = diff.currentValue == null ? "" : String(diff.currentValue);
  const inc = diff.incomingValue == null ? "" : String(diff.incomingValue);
  return cur === inc;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return String(iso); }
}

// ─── Diff state reducer ───────────────────────────────────────────────────────

function diffsReducer(state, action) {
  switch (action.type) {
    case "INIT":
      return new Map(action.diffs.map((d) => [d.id, d]));
    case "APPEND": {
      const next = new Map(state);
      for (const d of action.diffs) next.set(d.id, d);
      return next;
    }
    case "UPDATE_ONE": {
      const next = new Map(state);
      const existing = next.get(action.id);
      if (existing) next.set(action.id, { ...existing, status: action.status });
      return next;
    }
    case "UPDATE_MANY": {
      const next = new Map(state);
      for (const [id, status] of action.updates) {
        const existing = next.get(id);
        if (existing) next.set(id, { ...existing, status });
      }
      return next;
    }
    case "MERGE": {
      // Reload after a targeted operation (e.g. row delete).
      // Start from new Map(state) so decisions on unloaded pages are preserved —
      // the reload only fetches page 1 but the user may have already decided rows
      // on later pages. For entries the server returned, any local decision that
      // differs from the server state is kept so user work is not lost.
      //
      // deletedDiffIds (optional): an explicit list of diff IDs to remove from
      // state. Set by handleConfirmDelete to targeted-prune only the just-deleted
      // staged row's diffs (those that were on the previous page-1 response but
      // absent from the new one), so unmatched diffs from other loaded pages
      // are never accidentally dropped.
      const next = new Map(state);
      if (action.deletedDiffIds) {
        for (const id of action.deletedDiffIds) next.delete(id);
      }
      for (const d of action.diffs) {
        const existing = state.get(d.id);
        if (existing && existing.status !== d.status) {
          next.set(d.id, existing);
        } else {
          next.set(d.id, d);
        }
      }
      return next;
    }
    default:
      return state;
  }
}

// ─── Held-breakdown helpers ───────────────────────────────────────────────────

function fmtFieldName(name) {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// Admin-set schema fields that must never appear as excluded-field chips.
// Mirrors PROTECTED_ABSTRACTION_FIELDS in leasePdfAdapter.js / diffGenerator.js.
const PROTECTED_CHIP_FIELDS = new Set(["areaType", "region", "stage"]);

// Derives a held-item breakdown from the first loaded page of diffs after a
// session-wide bulk decide. Only the loaded page is used — session-wide totals
// cover the "still pending" remainder displayed in the stats bar.
export function computeHeldBreakdown(diffs) {
  const excludedByField = new Map();
  let newLeaseCount = 0;
  for (const d of diffs) {
    if (d.status !== "pending") continue;
    if (d.fieldName === "__new_lease") {
      newLeaseCount++;
    } else if (!PROTECTED_CHIP_FIELDS.has(d.fieldName)) {
      const label = fmtFieldName(d.fieldName);
      excludedByField.set(label, (excludedByField.get(label) || 0) + 1);
    }
  }
  return { excludedByField, newLeaseCount };
}

// ─── New leases section ───────────────────────────────────────────────────────
//
// Renders a summary row per new-lease candidate (__new_lease diff).
// incoming_value is the structured summary object from generateNewLeaseDiff.
// Add/Skip uses handleDecide(diffId, "accepted"/"rejected") so no state reset.

function fmtNewLeaseDate(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return s; }
}

function buildNewLeaseSummary(incomingValue) {
  const v = (incomingValue && typeof incomingValue === "object") ? incomingValue : {};
  const str = (x) => (x != null && x !== "" ? String(x).trim() : null);
  const leaseId  = str(v.leaseIdSpacebase);
  const locId    = str(v.locationId);
  const type     = str(v.type);
  const start    = fmtNewLeaseDate(v.startDate);
  const end      = fmtNewLeaseDate(v.endDate);
  const dateSpan = start && end ? `${start} – ${end}` : start || end || null;
  const cost     = v.totalCost != null ? Number(v.totalCost).toLocaleString("en-US") : null;
  const currency = str(v.currency);
  const parts = [leaseId, locId, type, dateSpan, cost, currency].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "(no identifying information)";
}

function NewLeasesSection({ newLeaseDiffs, onDecide, busy, skippedDetailsMap, containerRef }) {
  if (!newLeaseDiffs || newLeaseDiffs.length === 0) return null;

  return (
    <div
      ref={containerRef}
      data-testid="new-leases-section"
      style={{ marginTop: 20, marginBottom: 8 }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px",
        background: T.bluePale,
        borderRadius: "8px 8px 0 0",
        borderBottom: `1px solid ${T.blueSoft}`,
      }}>
        <span style={{ fontSize: T.fsMd, lineHeight: "20px", flexShrink: 0 }}>🆕</span>
        <span style={{ flex: 1, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.blueDeep }}>
          New leases (not yet in {APP_NAME})
        </span>
        <span style={{ fontSize: T.fsXxs, color: T.blueDeep }}>
          {newLeaseDiffs.length} candidate{newLeaseDiffs.length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={{ border: `1px solid ${T.blueSoft}`, borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
        {newLeaseDiffs.map((diff, idx) => {
          const summary  = buildNewLeaseSummary(diff.incomingValue);
          const isAdded  = diff.status === "accepted" && !diff.appliedAt;
          const isApplied = !!diff.appliedAt;
          const isSkipped = diff.status === "rejected";
          const isLast   = idx === newLeaseDiffs.length - 1;
          const skipError = skippedDetailsMap?.get(diff.id);

          return (
            <React.Fragment key={diff.id}>
            {diff.scheduleWarnings?.length > 0 && (
              <div role="alert" style={{ padding: 8, color: T.warnAmberText, background: T.warnAmberBg, fontSize: T.fsSm }}>
                {diff.scheduleWarnings.map((warning) => <div key={warning}>{warning}</div>)}
                Review individually before adding.
              </div>
            )}
            <div
              data-testid={`new-lease-row-${diff.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px",
                borderBottom: (isLast && !skipError) ? "none" : `1px solid ${T.border}`,
                background: (isAdded || isApplied) ? T.acceptedRowBg : isSkipped ? T.bg : T.white,
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: T.fsSm,
                  color: isSkipped ? T.light : T.dark,
                  textDecoration: isSkipped ? "line-through" : "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={summary}
              >
                {summary}
              </span>
              {isApplied ? <span>Added · {diff.leaseId}</span> : <button
                type="button"
                data-testid={`new-lease-add-btn-${diff.id}`}
                disabled={busy}
                onClick={() => onDecide(diff.id, isAdded ? "pending" : "accepted")}
                style={{
                  padding: "4px 12px",
                  borderRadius: 5,
                  fontSize: T.fsXs,
                  fontWeight: T.fwBold,
                  cursor: busy ? "not-allowed" : "pointer",
                  border: `1.5px solid ${isAdded ? T.successBorder : T.border}`,
                  background: isAdded ? T.utilGreen : T.white,
                  color: isAdded ? T.utilGreenText : T.mid,
                  opacity: busy ? 0.5 : 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {isAdded ? "✓ Add" : "Add"}
              </button>}
              {!isApplied && <button
                type="button"
                data-testid={`new-lease-skip-btn-${diff.id}`}
                disabled={busy}
                onClick={() => onDecide(diff.id, isSkipped ? "pending" : "rejected")}
                style={{
                  padding: "4px 10px",
                  borderRadius: 5,
                  fontSize: T.fsXs,
                  fontWeight: T.fwBold,
                  cursor: busy ? "not-allowed" : "pointer",
                  border: `1.5px solid ${isSkipped ? T.redRing : T.border}`,
                  background: isSkipped ? T.utilRed : T.white,
                  color: isSkipped ? T.breakRed : T.mid,
                  opacity: busy ? 0.5 : 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Skip
              </button>}
            </div>
            {skipError && (
              <div
                data-testid={`skip-error-${diff.id}`}
                role="alert"
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "5px 12px 5px 14px",
                  background: T.alertErrBg,
                  borderBottom: isLast ? "none" : `1px solid ${T.alertErrBorder}`,
                  fontSize: T.fsXs,
                  color: T.breakRed,
                }}
              >
                <span style={{ fontWeight: T.fwBold, flexShrink: 0 }}>⚠ Skipped</span>
                <span style={{ flex: 1 }}>
                  {skipError.error || "Could not be written due to a constraint error."}
                </span>
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Lease group ──────────────────────────────────────────────────────────────

function buildUnmatchedLabel(diffs) {
  const count = diffs.length;
  // Prefer a __new_lease diff with structured metadata — reuse buildNewLeaseSummary.
  const newLeaseDiff = diffs.find(
    (d) => d.fieldName === "__new_lease" && d.incomingValue && typeof d.incomingValue === "object"
  );
  if (newLeaseDiff) {
    const summary = buildNewLeaseSummary(newLeaseDiff.incomingValue);
    if (summary && summary !== "(no identifying information)") return summary;
  }
  return `Unmatched rows (${count})`;
}

function LeaseGroup({ leaseId, diffs, onDecide, onAcceptAll, stagedRows, onDeleteRow, busy, showUnchangedPage, skippedDetailsMap, expandTrigger }) {
  const [collapsed,          setCollapsed]          = useState(false);
  const [showUnchangedGroup, setShowUnchangedGroup] = useState(false);

  // Force-expand this group when the parent triggers it (e.g. via banner anchor click).
  useEffect(() => {
    if (expandTrigger) setCollapsed(false);
  }, [expandTrigger]);

  // Unmatched groups (leaseId=null) are display-only: their diffs cannot be
  // applied and should not be counted as pending decisions.
  const isUnmatched = leaseId == null;

  const hasSkippedDiff = diffs.some((d) => skippedDetailsMap?.has(d.id));

  const changedDiffs   = diffs.filter((d) => !isUnchangedDiff(d));
  const unchangedDiffs = diffs.filter((d) => isUnchangedDiff(d));
  const unchangedCount = unchangedDiffs.length;

  const visibleDiffs = showUnchangedPage && showUnchangedGroup
    ? diffs
    : changedDiffs;

  const applied  = diffs.filter((d) => d.appliedAt != null).length;
  // Unmatched diffs are never pending from the user's perspective — they need
  // to be deleted, not decided.
  const pending  = isUnmatched ? 0 : diffs.filter((d) => d.appliedAt == null && d.status === "pending").length;
  const accepted = isUnmatched ? 0 : diffs.filter((d) => d.appliedAt == null && d.status === "accepted").length;

  // Auto-collapse when all pending rows are resolved; auto-expand on rollback
  const prevPendingRef = useRef(null);
  useEffect(() => {
    if (prevPendingRef.current !== null) {
      if (prevPendingRef.current > 0 && pending === 0) {
        setCollapsed(true);
      } else if (prevPendingRef.current === 0 && pending > 0) {
        setCollapsed(false);
      }
    }
    prevPendingRef.current = pending;
  }, [pending]);

  return (
    <div
      data-testid={`lease-group-${leaseId}`}
      style={{
        marginBottom: 12,
        border: `1px solid ${hasSkippedDiff ? T.alertErrBorder : T.border}`,
        borderLeft: hasSkippedDiff ? `3px solid ${T.breakRed}` : `1px solid ${T.border}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* Group header */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 12px",
          background: T.bg,
          borderBottom: collapsed ? "none" : `1px solid ${T.border}`,
        }}
      >
        <button
          type="button"
          onClick={() => setCollapsed((p) => !p)}
          style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0,
          }}
        >
          <span style={{ fontSize: T.fsXs, color: T.light, marginRight: 2 }}>
            {collapsed ? "▶" : "▼"}
          </span>
          <span
            style={{ flex: 1, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, fontFamily: "monospace" }}
          >
            {leaseId && typeof leaseId === "string"
              ? leaseId
              : buildUnmatchedLabel(diffs)}
          </span>
          <span style={{ fontSize: T.fsXs, color: T.mid }}>
            {diffs.length} diff{diffs.length === 1 ? "" : "s"}
            {applied > 0  && ` · ${applied} applied`}
            {pending > 0  && ` · ${pending} pending`}
            {accepted > 0 && ` · ${accepted} accepted`}
            {pending === 0 && accepted > 0 && " ✓"}
          </span>
        </button>

        {!collapsed && showUnchangedPage && unchangedCount > 0 && (
          <button
            type="button"
            data-testid={`show-unchanged-group-${leaseId}`}
            onClick={() => setShowUnchangedGroup((p) => !p)}
            style={{
              padding: "3px 9px", borderRadius: 5, fontSize: T.fsXxs, fontWeight: T.fwSemibold,
              border: `1.5px solid ${showUnchangedGroup ? T.blue : T.border}`,
              background: showUnchangedGroup ? T.blueSoft : T.white,
              color: showUnchangedGroup ? T.blue : T.mid,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {showUnchangedGroup ? "Hide unchanged" : `Show unchanged (${unchangedCount})`}
          </button>
        )}

        {!isUnmatched && !busy && pending > 0 && (
          <button
            type="button"
            data-testid={`accept-all-lease-${leaseId}`}
            onClick={() => onAcceptAll(diffs.filter((d) => d.status === "pending").map((d) => d.id))}
            style={{
              padding: "3px 10px", borderRadius: 5, fontSize: T.fsXs, fontWeight: T.fwSemibold,
              border: `1.5px solid ${T.successBorder}`, background: T.utilGreen, color: T.utilGreenText,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Use all incoming ({pending})
          </button>
        )}
        {!busy && onDeleteRow && stagedRows?.map((sr, idx) => (
          <button
            key={sr.id}
            type="button"
            data-testid={`delete-staged-row-${sr.id}`}
            onClick={() => onDeleteRow(sr.id)}
            title="Remove this staged row from the import"
            style={{
              padding: "3px 9px", borderRadius: 5, fontSize: T.fsXxs, fontWeight: T.fwSemibold,
              border: `1.5px solid ${T.redRing}`, background: T.utilRed, color: T.breakRed,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {stagedRows.length > 1 ? `Delete row ${idx + 1}` : "Delete row"}
          </button>
        ))}
      </div>

      {/* Unmatched group callout — explains why no decisions are available */}
      {!collapsed && isUnmatched && (
        <div
          data-testid="unmatched-group-callout"
          style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "10px 12px",
            background: T.warnYellowBg,
            borderBottom: `1px solid ${T.warnYellowBorder}`,
          }}
        >
          <span style={{ fontSize: T.fsMd, lineHeight: "18px", flexShrink: 0 }}>⚠</span>
          <span style={{ fontSize: T.fsXs, color: T.amberDark, lineHeight: "18px" }}>
            These rows couldn't be matched to a lease and won't be applied.
            Delete the rows to remove them from this import.
          </span>
        </div>
      )}

      {/* Diff rows — shared CSS Grid so every DiffRow's cells align to the same tracks */}
      {!collapsed && (
        <div style={{ display: "grid", gridTemplateColumns: DIFF_GRID_COLS }}>

          {/* ── Sticky column header row ── */}
          {(["Field", `Current (in ${APP_NAME})`, "→", "Incoming (this import)", isUnmatched ? "" : "Decision"]).map((label, i) => (
            <div
              key={i}
              data-testid={i === 0 ? "diff-grid-header" : undefined}
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: T.bg,
                borderBottom: `1px solid ${T.borderDark}`,
                padding: "5px 10px",
                fontSize: T.fsXxs,
                fontWeight: T.fwBold,
                color: T.mid,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                whiteSpace: "nowrap",
                ...(i === 2 ? { textAlign: "center", padding: "5px 0" } : {}),
                ...(i === 4 ? { textAlign: "right" } : {}),
              }}
            >
              {label}
            </div>
          ))}

          {visibleDiffs.map((diff) => {
            const skipError = skippedDetailsMap?.get(diff.id);
            return (
              <React.Fragment key={diff.id}>
                <DiffRow
                  diff={diff}
                  onDecide={onDecide}
                  disabled={busy}
                  readOnly={isUnmatched}
                />
                {skipError && (
                  <div
                    data-testid={`skip-error-${diff.id}`}
                    role="alert"
                    style={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 12px 5px 14px",
                      background: T.alertErrBg,
                      borderBottom: `1px solid ${T.alertErrBorder}`,
                      fontSize: T.fsXs,
                      color: T.breakRed,
                    }}
                  >
                    <span style={{ fontWeight: T.fwBold, flexShrink: 0 }}>⚠ Skipped</span>
                    <span style={{ flex: 1 }}>
                      {skipError.error || "Could not be written due to a constraint error."}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {visibleDiffs.length === 0 && (
            <div style={{
              gridColumn: "1 / -1",
              padding: "12px 10px", fontSize: T.fsXs, color: T.light, textAlign: "center",
            }}>
              No changed diffs for this lease.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ isOpen, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      ariaLabelledBy="confirm-dialog-title"
      testId="confirm-dialog"
    >
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
        <span id="confirm-dialog-title" style={{ fontWeight: T.fwBold, fontSize: T.fsMd5, color: T.dark }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "16px 20px", fontSize: T.fsBase, color: T.mid }}>
        {message}
      </div>
      <div style={{
        padding: "12px 20px", borderTop: `1px solid ${T.border}`,
        display: "flex", justifyContent: "flex-end", gap: 10,
      }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "7px 16px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
            border: `1.5px solid ${T.border}`, background: T.white, color: T.mid, cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="confirm-action-btn"
          onClick={onConfirm}
          style={{
            padding: "7px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
            border: "none",
            background: danger ? T.red : T.blue,
            color: T.white, cursor: "pointer",
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ─── BadgingReviewSummary ───────────────────────────────────────────────────
// INGEST-PIPELINE-TYPE-AWARE-S1: Step 4 review for badging/utilization
// sessions. These never populate import_diffs, so instead of the lease diff
// list we show per-location "New/Updated badging months" counts sourced from
// GET /sessions/:id/badging-review.

function BadgingReviewSummary({
  review, loading, error, busy, applyError,
  onApply, onReject, confirmDialog, onConfirmReject, onCancelConfirm,
  isReadOnly, onBack,
}) {
  const [expandedLocs, setExpandedLocs] = useState(() => new Set());

  const toggleLoc = (locId) => {
    setExpandedLocs((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId); else next.add(locId);
      return next;
    });
  };

  if (loading && !review) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
        Loading badging review…
      </div>
    );
  }

  if (error && !review) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.errorText, fontSize: T.fsBase }}>
        {error?.message || String(error)}
      </div>
    );
  }

  if (!review) return null;

  const { reviewCopy, locations, totals } = review;

  if (review.canonical) {
    return (
      <div data-testid="canonical-badging-review" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{
          flexShrink: 0, padding: "14px 18px", background: T.bg,
          borderBottom: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
            {reviewCopy?.title || reviewCopy?.newLabel?.replace(/^New /, "") || "Canonical badging import"}
          </div>
          <div style={{ display: "flex", gap: 20, fontSize: T.fsXs5, color: T.mid }}>
            <span data-testid="canonical-badging-row-total">
              {review.rowsIn.toLocaleString()} rows in file
            </span>
            <span data-testid="canonical-badging-total-new" style={{ color: T.utilGreenText, fontWeight: T.fwSemibold }}>
              {totals.new.toLocaleString()} new
            </span>
            <span data-testid="canonical-badging-total-updated" style={{ color: T.blue, fontWeight: T.fwSemibold }}>
              {totals.updated.toLocaleString()} updated
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: T.fsXs5, color: T.mid }}>
            Target table: <code>{review.targetTable}</code>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 18px" }}>
          {review.rows.map((row) => (
            <div
              key={row.rowIndex}
              data-testid={`canonical-badging-row-${row.rowIndex}`}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "7px 10px",
                borderBottom: `1px solid ${T.border}`, fontSize: T.fsXs5,
              }}
            >
              <span style={{ minWidth: 58, color: T.mid }}>Row {row.rowIndex}</span>
              <span style={{ flex: 1, color: T.dark }}>
                {Object.entries(row)
                  .filter(([key]) => !["rowIndex", "status", "_sourceRow", "_validationErrors"].includes(key))
                  .slice(0, 4)
                  .map(([key, value]) => `${key}: ${value ?? "—"}`)
                  .join(" · ")}
              </span>
              <span style={{
                color: row.status === "new" ? T.utilGreenText : T.blue,
                fontWeight: T.fwSemibold,
              }}>
                {row.status === "new" ? "New" : "Updated"}
              </span>
            </div>
          ))}
        </div>
        {!isReadOnly && (
          <div style={{
            flexShrink: 0, padding: "12px 18px", borderTop: `1px solid ${T.border}`,
            display: "flex", justifyContent: "flex-end", gap: 10,
          }}>
            {applyError && (
              <div role="alert" data-testid="badging-apply-error" style={{
                marginRight: "auto", padding: "10px 14px", background: T.alertErrBg,
                border: `1px solid ${T.alertErrBorder}`, borderRadius: 6,
                color: T.dark, fontSize: T.fsSm,
              }}>
                Apply failed: {applyError?.message || String(applyError)}
              </div>
            )}
            <button type="button" data-testid="badging-reject-btn" onClick={onReject} disabled={busy}
              style={{ padding: "9px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.border}`, background: T.white, color: T.mid }}>
              Reject
            </button>
            <button type="button" data-testid="badging-apply-btn" onClick={onApply} disabled={busy}
              style={{ padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: "none", background: T.blue, color: T.white, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Applying…" : "Apply"}
            </button>
          </div>
        )}
        {isReadOnly && (
          <div style={{ flexShrink: 0, padding: "12px 18px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end" }}>
            <button type="button" data-testid="badging-done-btn" onClick={onBack}
              style={{ padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: "none", background: T.blue, color: T.white }}>
              Done
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="badging-review-summary" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        flexShrink: 0, padding: "14px 18px", background: T.bg,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
          {reviewCopy?.title || "Badging / utilization import"}
        </div>
        {reviewCopy?.description && (
          <div style={{ fontSize: T.fsXs5, color: T.mid, marginBottom: 10 }}>
            {reviewCopy.description}
          </div>
        )}
        <div style={{ display: "flex", gap: 20, fontSize: T.fsXs5 }}>
          <span data-testid="badging-total-new" style={{ color: T.utilGreenText, fontWeight: T.fwSemibold }}>
            {totals.new.toLocaleString()} new
          </span>
          <span data-testid="badging-total-updated" style={{ color: T.blue, fontWeight: T.fwSemibold }}>
            {totals.updated.toLocaleString()} updated
          </span>
          {totals.flagged > 0 && (
            <span data-testid="badging-total-flagged" style={{ color: T.warnAmberText, fontWeight: T.fwSemibold }}>
              {totals.flagged.toLocaleString()} flagged
            </span>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "10px 18px" }}>
        {locations.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
            No badging rows staged for this session.
          </div>
        )}
        {locations.map((loc) => {
          const isOpen = expandedLocs.has(loc.locationId);
          return (
            <div
              key={loc.locationId}
              data-testid={`badging-location-row-${loc.locationId}`}
              style={{
                border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 8, overflow: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => toggleLoc(loc.locationId)}
                data-testid={`badging-location-toggle-${loc.locationId}`}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", background: T.white, border: "none", cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
                  {loc.locationName}
                </span>
                <span style={{ display: "flex", gap: 12, fontSize: T.fsXs5, color: T.mid }}>
                  {loc.newCount > 0 && (
                    <span style={{ color: T.utilGreenText }}>{loc.newCount} new</span>
                  )}
                  {loc.updatedCount > 0 && (
                    <span style={{ color: T.blue }}>{loc.updatedCount} updated</span>
                  )}
                  <span>{isOpen ? "▲" : "▼"}</span>
                </span>
              </button>
              {isOpen && (
                <ul style={{ margin: 0, padding: "0 14px 10px", listStyle: "none" }}>
                  {loc.months.map((m) => (
                    <li
                      key={`${m.month}-${m.sourceSystem}`}
                      data-testid={`badging-month-row-${loc.locationId}-${m.month}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "5px 0", borderTop: `1px solid ${T.border}`,
                        fontSize: T.fsXs5,
                      }}
                    >
                      <span style={{ minWidth: 80, color: T.mid, fontFamily: "monospace" }}>{m.month}</span>
                      <span style={{
                        minWidth: 56, fontWeight: T.fwSemibold,
                        color: m.status === "new" ? T.utilGreenText : T.blue,
                      }}>
                        {m.status === "new" ? "New" : "Updated"}
                      </span>
                      <span style={{ color: T.dark }}>
                        avg {m.avgDailyUniqueBadges ?? "—"} · peak {m.peakDailyUniqueBadges ?? "—"}
                      </span>
                      {m.anomaly && (
                        <span style={{
                          color: m.anomalyAcknowledged ? T.mid : T.warnAmberText,
                          fontWeight: T.fwSemibold,
                        }}>
                          {m.anomalyAcknowledged ? "⚠ acknowledged" : "⚠ peak > 3× avg"}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {!isReadOnly && (
        <div style={{ flexShrink: 0 }}>
          {applyError && (
            <div
              role="alert"
              data-testid="badging-apply-error"
              style={{
                margin: "0 18px", marginTop: 12, padding: "10px 14px",
                background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 6,
                color: T.dark, fontSize: T.fsSm,
              }}
            >
              Apply failed: {applyError?.message || String(applyError)}. Nothing was written - you can try again.
            </div>
          )}
          <div style={{
            padding: "12px 18px", borderTop: `1px solid ${T.border}`,
            display: "flex", justifyContent: "flex-end", gap: 10,
          }}>
            <button
              type="button"
              data-testid="badging-reject-btn"
              onClick={onReject}
              disabled={busy}
              style={{
                padding: "9px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.border}`, background: T.white, color: T.mid,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              Reject
            </button>
            <button
              type="button"
              data-testid="badging-apply-btn"
              onClick={onApply}
              disabled={busy}
              style={{
                padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: "none", background: T.blue, color: T.white,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "Applying…" : "Apply to utilization"}
            </button>
          </div>
        </div>
      )}

      {isReadOnly && (
        <div style={{
          flexShrink: 0, padding: "12px 18px", borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "flex-end",
        }}>
          <button
            type="button"
            data-testid="badging-done-btn"
            onClick={onBack}
            style={{
              padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              border: "none", background: T.blue, color: T.white, cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog?.type === "reject"}
        title="Reject entire session?"
        message="All staged badging rows in this session will be rejected. No changes will be written to utilization. This cannot be undone."
        confirmLabel="Reject session"
        danger
        onConfirm={onConfirmReject}
        onCancel={onCancelConfirm}
      />
    </div>
  );
}

// ─── ImportReviewPanel ────────────────────────────────────────────────────────

const PAGE_SIZE = 200;
const FILTER_OPTIONS = [
  { value: "all",      label: "All"      },
  { value: "pending",  label: "Pending"  },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "applied",  label: "Applied"  },
];

export function ImportReviewPanel({ sessionId, onBack, onRestage, onTitleChange, onApplied }) {
  const [session,     setSession]     = useState(null);
  const [diffsMap,    dispatchDiffs]  = useReducer(diffsReducer, new Map());
  const [totals,      setTotals]      = useState({ pending: 0, accepted: 0, rejected: 0, applied: 0 });
  const [pagination,  setPagination]  = useState({ offset: 0, limit: PAGE_SIZE, total: 0 });
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);
  const [bulkError,   setBulkError]   = useState(null);
  const [busy,        setBusy]        = useState(false);
  const [filter,      setFilter]      = useState("all");
  const [resumedAt,   setResumedAt]   = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [applyResult,        setApplyResult]        = useState(null);
  const [applyError,         setApplyError]         = useState(null);
  const [skippedDetailsMap,  setSkippedDetailsMap]  = useState(new Map());
  const [partialApplyBanner, setPartialApplyBanner] = useState(null);
  const [dirtyState,    setDirtyState]    = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  // ── ui_v2 bulk-decide clarity state ───────────────────────────────────────
  const [heldBreakdown,    setHeldBreakdown]    = useState(null);
  const [bulkConfirmation, setBulkConfirmation] = useState(null);
  const [bulkDecideDone,   setBulkDecideDone]   = useState(false);

  // ── Loaded-slice server snapshots (for session-wide counter adjustment) ──
  const [loadedServerPending,  setLoadedServerPending]  = useState(0);
  const [loadedServerAccepted, setLoadedServerAccepted] = useState(0);
  const [loadedServerRejected, setLoadedServerRejected] = useState(0);

  // ── Expired-state discard (hoisted to satisfy Rules of Hooks) ─────────────
  const [expiredDiscarding, setExpiredDiscarding] = useState(false);
  const [expiredDiscardErr, setExpiredDiscardErr] = useState(null);

  // ── Expand triggers for skipped-lease anchor clicks ────────────────────────
  // Maps leaseId → monotonic counter; LeaseGroup watches for changes and
  // force-expands its accordion when the counter increments.
  const [expandTriggers, setExpandTriggers] = useState({});

  // stagedRowsMap: leaseIdSpacebase → { id, row_index, normalized_data }
  const [stagedRowsMap, setStagedRowsMap] = useState(new Map());

  // ── INGEST-PIPELINE-TYPE-AWARE-S1: badging/utilization sessions bypass the
  // import_diffs table entirely — they get their own review summary instead
  // of the lease diff list below.
  const [badgingReview,      setBadgingReview]      = useState(null);
  const [badgingLoading,     setBadgingLoading]     = useState(false);
  const [badgingError,       setBadgingError]       = useState(null);

  // ── INGEST-HEADCOUNT-TYPE-S1: headcount sessions also bypass import_diffs.
  const [headcountReview,    setHeadcountReview]    = useState(null);
  const [headcountLoading,   setHeadcountLoading]   = useState(false);
  const [headcountError,     setHeadcountError]     = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  // Keep a live ref to diffsMap so loadMore callbacks can read the current
  // state without stale closures (loadMore deps exclude diffsMap for perf).
  const diffsMapRef = useRef(diffsMap);
  useEffect(() => { diffsMapRef.current = diffsMap; }, [diffsMap]);

  // Scroll targets for the "Review held items" link (ui_v2).
  const heldItemsRef        = useRef(null);
  const newLeasesSectionRef = useRef(null);

  // Tracks the unmatched diff IDs (leaseId=null) that were present in the
  // most recent page-1 server response. Used by handleConfirmDelete to compute
  // which unmatched diffs belong to the just-deleted row (i.e. those in
  // page1UnmatchedIdsRef that are now absent from the new page-1 response).
  const page1UnmatchedIdsRef = useRef(new Set());

  const localTotals = useCallback(() => {
    let pending = 0, accepted = 0, rejected = 0, applied = 0;
    for (const d of diffsMap.values()) {
      // Only a selected __new_lease is actionable among unmatched diffs.
      if (d.leaseId == null && d.fieldName !== "__new_lease") continue;
      if (d.appliedAt != null)          applied++;
      else if (d.status === "pending")  pending++;
      else if (d.status === "accepted") accepted++;
      else if (d.status === "rejected") rejected++;
    }
    return { pending, accepted, rejected, applied };
  }, [diffsMap]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  function buildStagedRowsMap(rows) {
    const m = new Map();
    for (const r of rows) {
      const key = r.matched_lease_id ?? null;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return m;
  }

  // ── Initial load ──────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 }),
      getStagedRows(sessionId).catch(() => ({ rows: [] })),
    ])
      .then(([{ session: s, diffs, totals: t, pagination: p, resumedAt: ra }, stagedRes]) => {
        if (cancelled) return;
        setSession(s);
        dispatchDiffs({ type: "INIT", diffs });
        setTotals(t);
        setPagination(p);
        setLoadedServerPending(diffs.filter((d) => d.status === "pending" && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted" && !d.appliedAt && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
        setHeldBreakdown(computeHeldBreakdown(diffs));
        setStagedRowsMap(buildStagedRowsMap(stagedRes.rows ?? []));
        page1UnmatchedIdsRef.current = new Set(
          diffs.filter((d) => d.leaseId == null).map((d) => d.id)
        );
        if (ra) setResumedAt(ra);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Badging review summary fetch ──────────────────────────────────────────
  useEffect(() => {
    if (!session || session.dataType !== "badging") return;
    let cancelled = false;
    setBadgingLoading(true);
    setBadgingError(null);
    getBadgingReview(sessionId)
      .then((data) => { if (!cancelled) setBadgingReview(data); })
      .catch((err) => { if (!cancelled) setBadgingError(err); })
      .finally(() => { if (!cancelled) setBadgingLoading(false); });
    return () => { cancelled = true; };
  }, [session, sessionId]);

  // ── Headcount review summary fetch ────────────────────────────────────────
  useEffect(() => {
    if (!session || session.dataType !== "headcount") return;
    let cancelled = false;
    setHeadcountLoading(true);
    setHeadcountError(null);
    getHeadcountReview(sessionId)
      .then((data) => { if (!cancelled) setHeadcountReview(data); })
      .catch((err) => { if (!cancelled) setHeadcountError(err); })
      .finally(() => { if (!cancelled) setHeadcountLoading(false); });
    return () => { cancelled = true; };
  }, [session, sessionId]);

  // ── Notify parent of title (for Modal variant) ────────────────────────────

  useEffect(() => {
    if (!session || !onTitleChange) return;
    const title = session.sourceFilename || "Import Review";
    const subTitle = [
      SOURCE_LABELS[session.sourceType] || session.sourceType,
      `Uploaded ${fmtDate(session.uploadedAt)}`,
      STATUS_LABELS[session.status] || session.status,
    ].filter(Boolean).join(" · ");
    onTitleChange(title, subTitle);
  }, [session, onTitleChange]);

  // ── Load more pages ───────────────────────────────────────────────────────

  const hasMore = pagination.offset + pagination.limit < pagination.total;

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    const nextOffset = pagination.offset + pagination.limit;
    setLoadingMore(true);
    getImportSession(sessionId, { limit: PAGE_SIZE, offset: nextOffset })
      .then(({ diffs, totals: t, pagination: p }) => {
        // Build the post-APPEND merged map manually (mirrors the APPEND reducer)
        // so that the server-baseline counts are derived from the full combined
        // set, not accumulated via existingCounts + newCounts. This prevents
        // drift when a diff ID appears on both the current map and the new page.
        const mergedMap = new Map(diffsMapRef.current);
        for (const d of diffs) mergedMap.set(d.id, d);
        let newServerPending = 0, newServerAccepted = 0, newServerRejected = 0;
        for (const d of mergedMap.values()) {
          if (d.status === "pending")  newServerPending++;
          else if (d.status === "accepted") newServerAccepted++;
          else if (d.status === "rejected") newServerRejected++;
        }

        dispatchDiffs({ type: "APPEND", diffs });
        setTotals(t);
        setPagination(p);
        setLoadedServerPending(newServerPending);
        setLoadedServerAccepted(newServerAccepted);
        setLoadedServerRejected(newServerRejected);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  }, [sessionId, pagination, loadingMore]);

  // ── Single diff decide (optimistic) ──────────────────────────────────────

  const handleDecide = useCallback(async (diffId, decision) => {
    dispatchDiffs({ type: "UPDATE_ONE", id: diffId, status: decision });
    setBulkError(null);
    try {
      await decideImportDiff(diffId, decision);
    } catch {
      const original = diffsMap.get(diffId);
      if (original) dispatchDiffs({ type: "UPDATE_ONE", id: diffId, status: original.status });
      setBulkError({
        error: new Error("The decision could not be saved. Please try again."),
        severity: "error",
      });
    }
  }, [diffsMap]);

  // ── Session-wide bulk decide (global "Use all incoming / Keep all current") ─

  const handleBulkSession = useCallback(async (decision) => {
    if (busy) return;
    setBusy(true);
    setBulkError(null);
    setConfirmDialog(null);

    const updates = [];
    for (const d of diffsMap.values()) {
      if (d.status !== "pending") continue;
      if (decision === "accepted" && d.canAccept === false) continue;
      if (decision === "accepted" && d.scheduleWarnings?.length) continue;
      updates.push([d.id, decision]);
    }
    dispatchDiffs({ type: "UPDATE_MANY", updates });

    try {
      await bulkDecideImportDiffs({ sessionId, decision });
      const { diffs, totals: t, pagination: p } =
        await getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 });
      dispatchDiffs({ type: "INIT", diffs });
      setTotals(t);
      setPagination(p);
      setLoadedServerPending(diffs.filter((d) => d.status === "pending").length);
      setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted").length);
      setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
      setDirtyState(false);
      // ── ui_v2: set reconciliation state after a successful bulk session ──
      const breakdown = computeHeldBreakdown(diffs);
      setHeldBreakdown(breakdown);
      setBulkDecideDone(true);
      const totalProcessed = t.pending + t.accepted + t.rejected + (t.applied || 0);
      setBulkConfirmation(
        `All ${totalProcessed.toLocaleString()} processed - ${t.accepted.toLocaleString()} accepted, ${t.pending.toLocaleString()} held (see below)`
      );
    } catch {
      setBulkError({
        error: new Error("The action could not be saved. Please try again."),
        severity: "error",
      });
      getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 })
        .then(({ diffs, totals: t, pagination: p }) => {
          dispatchDiffs({ type: "INIT", diffs });
          setTotals(t);
          setPagination(p);
          setLoadedServerPending(diffs.filter((d) => d.status === "pending").length);
          setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted").length);
          setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
          setDirtyState(false);
        })
        .catch((err) => {
          setDirtyState(true);
          log.warn("[Warn] Failed to reload diffs after bulk-session rollback", { sessionId, err });
          setBulkError({
            error: new Error("The action failed and the page could not be refreshed - some decisions may appear out of date. Please refresh the page manually."),
            severity: "warning",
          });
        });
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, diffsMap]);

  // ── Scroll to held items (ui_v2) ──────────────────────────────────────────

  const scrollToHeldItems = useCallback(() => {
    const target = heldItemsRef.current ?? newLeasesSectionRef.current;
    if (target?.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // ── Scroll to a specific lease group and force-expand its accordion ────────

  const handleScrollToLease = useCallback((leaseId) => {
    const el = document.querySelector(`[data-lease-group-id="${leaseId}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setExpandTriggers((prev) => ({ ...prev, [leaseId]: (prev[leaseId] || 0) + 1 }));
  }, []);

  // ── Bulk decide ───────────────────────────────────────────────────────────

  const handleBulk = useCallback(async (action, extraDiffIds) => {
    if (busy) return;
    setBusy(true);
    setBulkError(null);

    let body;
    switch (action) {
      case "accept_recommended":
        body = { sessionId, decision: "accepted", recommendedAction: "apply" };
        break;
      case "accept_all":
        body = { sessionId, decision: "accepted" };
        break;
      case "reject_all":
        body = { sessionId, decision: "rejected" };
        break;
      case "accept_null_fills":
        body = { sessionId, decision: "accepted", nullFillsOnly: true };
        break;
      case "accept_spacebase":
        body = { sessionId, decision: "accepted", currentProvenance: ["s", "spacebase"], diffType: "value_change" };
        break;
      case "reject_manual":
        body = { sessionId, decision: "rejected", currentProvenance: ["m", "manual"], diffType: "value_change" };
        break;
      case "accept_promotions":
        body = { sessionId, decision: "accepted", diffType: "provenance_promotion" };
        break;
      case "accept_lease":
        body = { sessionId, decision: "accepted", diffIds: extraDiffIds || [] };
        break;
      default:
        setBusy(false);
        return;
    }

    const decision = body.decision;
    const updates = [];
    for (const d of diffsMap.values()) {
      if (d.status !== "pending") continue;
      if (decision === "accepted" && (d.scheduleWarnings?.length || d.canAccept === false)) continue;
      if (action === "accept_recommended"  && d.recommendedAction !== "apply") continue;
      if (action === "accept_null_fills"   && d.currentValue !== null)          continue;
      if (action === "accept_spacebase"    && !["s","spacebase"].includes(d.currentProvenance)) continue;
      if (action === "reject_manual"       && !["m","manual"].includes(d.currentProvenance))    continue;
      if (action === "accept_promotions"   && d.diffType !== "provenance_promotion")             continue;
      if (action === "accept_lease"        && !(extraDiffIds || []).includes(d.id))              continue;
      updates.push([d.id, decision]);
    }
    dispatchDiffs({ type: "UPDATE_MANY", updates });

    try {
      await bulkDecideImportDiffs(body);
    } catch {
      setBulkError({
        error: new Error("The action could not be saved. Please try again."),
        severity: "error",
      });
      getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 })
        .then(({ diffs, totals: t, pagination: p }) => {
          dispatchDiffs({ type: "INIT", diffs });
          setTotals(t);
          setPagination(p);
          setLoadedServerPending(diffs.filter((d) => d.status === "pending").length);
          setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted").length);
          setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
          setDirtyState(false);
        })
        .catch((err) => {
          setDirtyState(true);
          log.warn("[Warn] Failed to reload diffs during bulk-decision rollback", { sessionId, err });
          setBulkError({
            error: new Error("The action failed and the page could not be refreshed - some decisions may appear out of date. Please refresh the page manually."),
            severity: "warning",
          });
        });
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, diffsMap]);

  // ── Manual reload ─────────────────────────────────────────────────────────

  const handleReload = useCallback(() => {
    setBusy(true);
    setError(null);
    setBulkError(null);
    Promise.all([
      getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 }),
      getStagedRows(sessionId).catch(() => ({ rows: [] })),
    ])
      .then(([{ session: s, diffs, totals: t, pagination: p }, stagedRes]) => {
        setSession(s);
        dispatchDiffs({ type: "INIT", diffs });
        setTotals(t);
        setPagination(p);
        setLoadedServerPending(diffs.filter((d) => d.status === "pending" && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted" && !d.appliedAt && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
        setStagedRowsMap(buildStagedRowsMap(stagedRes.rows ?? []));
        setDirtyState(false);
      })
      .catch((err) => setError(err))
      .finally(() => setBusy(false));
  }, [sessionId]);

  // ── Delete staged row ─────────────────────────────────────────────────────

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDialog?.rowId) return;
    setConfirmDialog(null);
    setBusy(true);
    try {
      await deleteStagedRow(sessionId, confirmDialog.rowId);
      // Reload the diff list but MERGE with current state so decisions already
      // made on other rows are not wiped out.
      const [{ session: s, diffs, totals: t, pagination: p }, stagedRes] = await Promise.all([
        getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 }),
        getStagedRows(sessionId).catch(() => ({ rows: [] })),
      ]);
      setSession(s);
      // Narrow prune: only remove unmatched diffs that (a) were on the previous
      // page-1 response (tracked in page1UnmatchedIdsRef) AND (b) are absent from
      // the new page-1 response. This targets only the just-deleted row's diffs
      // while leaving unmatched diffs loaded from later pages untouched.
      const newServerIds = new Set(diffs.map((d) => d.id));
      const deletedUnmatchedIds = [...page1UnmatchedIdsRef.current].filter(
        (id) => !newServerIds.has(id)
      );
      page1UnmatchedIdsRef.current = new Set(
        diffs.filter((d) => d.leaseId == null).map((d) => d.id)
      );
      dispatchDiffs({ type: "MERGE", diffs, deletedDiffIds: deletedUnmatchedIds });
      setTotals(t);
      setPagination(p);
      setLoadedServerPending(diffs.filter((d) => d.status === "pending").length);
      setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted").length);
      setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
      setStagedRowsMap(buildStagedRowsMap(stagedRes.rows ?? []));
      setDirtyState(false);
    } catch (err) {
      setBulkError({ error: new Error(err.message || "Failed to delete staged row."), severity: "error" });
    } finally {
      setBusy(false);
    }
  }, [sessionId, confirmDialog]);

  // ── Apply session ─────────────────────────────────────────────────────────

  const handleApply = async () => {
    setConfirmDialog(null);
    setBusy(true);
    setSkippedDetailsMap(new Map());
    setPartialApplyBanner(null);
    setApplyError(null);
    try {
      const result = await applyImportSession(sessionId);
      const hasSkipped = Array.isArray(result.skippedDetails) && result.skippedDetails.length > 0;
      // INGEST-SUMMARY-SCREEN-S1: badging responses have no `status` field
      // (the fast-path always fully applies — there is no pending/partial
      // concept for badging), so treat status-less + no-skips as clean too.
      const isCleanApply = (result.status === "applied" || result.status === undefined) && !hasSkipped;

      if (isCleanApply) {
        showToast("Import applied.", { type: "success" });
        if (onApplied) {
          // Wizard context: let the parent drive navigation to its own
          // Step 5 summary screen instead of showing the inline success screen.
          onApplied(result);
        } else {
          // Standalone context (imports queue / modal review): keep the
          // existing inline success screen unchanged.
          setApplyResult(result);
        }
      } else {
        // Partial apply (some diffs could not be written) — stay on the review
        // panel so the reviewer can act on the skipped rows.
        if (hasSkipped) {
          setSkippedDetailsMap(
            new Map(result.skippedDetails.map(({ diffId, field, error, reason }) => [diffId, { field, error: error || reason }]))
          );
          setPartialApplyBanner({ applied: result.applied ?? 0, skipped: result.skipped ?? result.skippedDetails.length });
        }
        const { session: s, diffs, totals: t, pagination: p } =
          await getImportSession(sessionId, { limit: PAGE_SIZE, offset: 0 });
        setSession(s);
        dispatchDiffs({ type: "INIT", diffs });
        setTotals(t);
        setPagination(p);
        setLoadedServerPending(diffs.filter((d) => d.status === "pending" && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerAccepted(diffs.filter((d) => d.status === "accepted" && !d.appliedAt && (d.leaseId != null || d.fieldName === "__new_lease")).length);
        setLoadedServerRejected(diffs.filter((d) => d.status === "rejected").length);
      }
    } catch (err) {
      console.log("[Apply] threw", err);
      setError(err);
      setApplyError(err);
    } finally {
      setBusy(false);
    }
  };

  // ── Reject session ────────────────────────────────────────────────────────

  const handleReject = async () => {
    setConfirmDialog(null);
    setBusy(true);
    try {
      await rejectImportSession(sessionId);
      onBack();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // ── Expired-state discard ─────────────────────────────────────────────────

  const handleExpiredDiscard = useCallback(async () => {
    setExpiredDiscarding(true);
    setExpiredDiscardErr(null);
    try {
      await discardImportSession(sessionId);
      onBack();
    } catch (err) {
      setExpiredDiscardErr(err?.message || "Discard failed. Please try again.");
      setExpiredDiscarding(false);
    }
  }, [sessionId, onBack]);

  // ── Derived state ─────────────────────────────────────────────────────────

  const live = localTotals();

  // Session-wide counts: adjust server totals by the delta the user has
  // introduced on the loaded slice since the last fetch.
  const sessionPending  = Math.max(0, totals.pending  - loadedServerPending  + live.pending);
  const sessionAccepted = Math.max(0, totals.accepted - loadedServerAccepted + live.accepted);
  const sessionRejected = Math.max(0, totals.rejected - loadedServerRejected + live.rejected);

  const acceptedCount = sessionAccepted;
  const isReadOnly = session && ["applied", "rejected"].includes(session.status);

  const allDiffs  = [...diffsMap.values()];
  const filtered  = filter === "all"
    ? allDiffs
    : filter === "applied"
      ? allDiffs.filter((d) => d.appliedAt != null)
      : filter === "rejected"
        ? allDiffs.filter((d) => d.status === "rejected" || skippedDetailsMap.has(d.id))
        : allDiffs.filter((d) => d.status === filter);

  // Derive the set of lease IDs that have at least one constraint-error skip.
  // Used by the partial-apply banner to name the affected leases.
  const skippedLeaseIds = (() => {
    if (skippedDetailsMap.size === 0) return [];
    const seen = new Set();
    const ids = [];
    for (const [diffId] of skippedDetailsMap) {
      const diff = diffsMap.get(diffId);
      if (diff?.leaseId && !seen.has(diff.leaseId)) {
        seen.add(diff.leaseId);
        ids.push(diff.leaseId);
      }
    }
    return ids;
  })();

  // Separate __new_lease diffs (shown in the New leases section) from
  // field-level diffs (shown in the LeaseGroup list). Any other leaseId=null
  // diffs (e.g. year_costs for unmatched rows) are also excluded from the main
  // list — they are auxiliary data attached to the __new_lease candidate.
  const newLeaseDiffs = allDiffs.filter((d) => d.fieldName === "__new_lease");

  const grouped = [];
  const seen    = new Map();
  for (const d of filtered) {
    if (d.leaseId == null || d.fieldName === "__new_lease") continue;
    const key = d.leaseId;
    if (!seen.has(key)) { seen.set(key, []); grouped.push({ leaseId: d.leaseId, diffs: seen.get(key) }); }
    seen.get(key).push(d);
  }

  // ── Expired-session state ─────────────────────────────────────────────────

  if (!loading && session?.status === "expired") {
    return (
      <div
        data-testid="expired-session-state"
        style={{ padding: "40px 32px", textAlign: "center" }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: T.bg, border: `2px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", fontSize: T.fsXl5,
        }}>
          ⏱
        </div>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
          Session expired
        </div>
        <div style={{ fontSize: T.fsBase, color: T.mid, marginBottom: 24, lineHeight: 1.5 }}>
          The staged rows for this session are no longer available - this can happen when the server
          restarts before diffs are saved. Re-upload the original file to start a new review session.
        </div>
        {expiredDiscardErr && (
          <div style={{ marginBottom: 16, fontSize: T.fsXs, color: T.breakRed }}>
            {expiredDiscardErr}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="expired-restage-btn"
            onClick={onRestage ?? onBack}
            style={{
              padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              border: "none", background: T.blue, color: T.white, cursor: "pointer",
            }}
          >
            Re-stage
          </button>
          <button
            type="button"
            data-testid="expired-discard-btn"
            onClick={handleExpiredDiscard}
            disabled={expiredDiscarding}
            style={{
              padding: "9px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              border: `1.5px solid ${T.border}`,
              background: T.bg, color: expiredDiscarding ? T.light : T.mid,
              cursor: expiredDiscarding ? "not-allowed" : "pointer",
              opacity: expiredDiscarding ? 0.6 : 1,
            }}
          >
            {expiredDiscarding ? "Discarding…" : "Discard"}
          </button>
        </div>
      </div>
    );
  }

  // ── Apply-success screen ──────────────────────────────────────────────────

  if (applyResult) {
    return (
      <div
        data-testid="apply-success-screen"
        style={{ padding: "40px 32px", textAlign: "center" }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: T.utilGreen, display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", fontSize: T.fsXl5, color: T.utilGreenText,
        }}>
          ✓
        </div>
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
          Diffs applied successfully
        </div>
        <div style={{ fontSize: T.fsBase, color: T.mid, marginBottom: 24, lineHeight: 1.5 }}>
          {applyResult.skipped > 0 ? (
            <>
              <strong>Added {(applyResult.receipt?.added ?? 0).toLocaleString()} leases · Applied {applyResult.applied.toLocaleString()} changes · Skipped {applyResult.skipped.toLocaleString()}</strong>
              <span> — {applyResult.skipped.toLocaleString()} diff{applyResult.skipped === 1 ? "" : "s"} could not be written due to constraint errors and remain in reviewable state.</span>
            </>
          ) : (
            <>
              <strong>{(applyResult.receipt?.added ?? 0).toLocaleString()}</strong> leases added · <strong>{(applyResult.receipt?.updated ?? 0).toLocaleString()}</strong> leases updated.
            </>
          )}
          {applyResult.pending > 0 && (
            <span> {applyResult.pending.toLocaleString()} pending diff{applyResult.pending === 1 ? "" : "s"} remain for the next review pass.</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="apply-success-done-btn"
            onClick={onBack}
            style={{
              padding: "9px 22px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              border: "none", background: T.blue, color: T.white, cursor: "pointer",
            }}
          >
            Done
          </button>
          <a
            href="#"
            data-testid="apply-success-history-link"
            onClick={(e) => { e.preventDefault(); onBack(); }}
            style={{
              padding: "9px 16px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
              color: T.blue, textDecoration: "underline", cursor: "pointer", display: "inline-block",
            }}
          >
            View ingestion history
          </a>
        </div>
      </div>
    );
  }

  // ── Loading / hard-error (no session yet) ─────────────────────────────────

  if (loading && !session) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
        Loading session…
      </div>
    );
  }

  if (error && !session) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.errorText, fontSize: T.fsBase }}>
        {error?.message || String(error)}
      </div>
    );
  }

  // ── Badging/utilization review summary ────────────────────────────────────
  // INGEST-PIPELINE-TYPE-AWARE-S1: entirely separate from the lease diff UI —
  // badging sessions never populate import_diffs.

  if (session?.dataType === "badging") {
    return (
      <BadgingReviewSummary
        session={session}
        review={badgingReview}
        loading={badgingLoading}
        error={badgingError}
        applyError={applyError}
        busy={busy}
        onApply={handleApply}
        onReject={() => setConfirmDialog({ type: "reject" })}
        confirmDialog={confirmDialog}
        onConfirmReject={handleReject}
        onCancelConfirm={() => setConfirmDialog(null)}
        isReadOnly={isReadOnly}
        onBack={onBack}
      />
    );
  }

  // ── Headcount review summary ──────────────────────────────────────────────
  // INGEST-HEADCOUNT-TYPE-S1: also entirely separate from the lease diff UI —
  // headcount sessions never populate import_diffs.

  if (session?.dataType === "headcount") {
    return (
      <HeadcountReviewSummary
        session={session}
        review={headcountReview}
        loading={headcountLoading}
        error={headcountError}
        applyError={applyError}
        busy={busy}
        onApply={handleApply}
        onReject={() => setConfirmDialog({ type: "reject" })}
        confirmDialog={confirmDialog}
        onConfirmReject={handleReject}
        onCancelConfirm={() => setConfirmDialog(null)}
        isReadOnly={isReadOnly}
        onBack={onBack}
      />
    );
  }

  // ── Main review panel ─────────────────────────────────────────────────────

  return (
    <>
      {session?.costBasisNotice && (
        <div role="note" data-testid="cost-basis-notice" style={{
          padding: "9px 18px", background: T.warnYellowBg, color: T.amberDark,
          borderBottom: `1px solid ${T.border}`, fontSize: T.fsSm,
        }}>
          {session.costBasisNotice}
        </div>
      )}
      {/* ── Stats bar ── */}
      <div style={{
        flexShrink: 0,
        display: "flex", alignItems: "center", gap: 20,
        padding: "8px 18px",
        background: T.bg,
        borderBottom: `1px solid ${T.border}`,
        flexWrap: "wrap",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: T.dark }}>{(session?.diffCount || 0).toLocaleString()}</div>
          <div style={{ fontSize: T.fsXxs, color: T.mid }}>total diffs</div>
        </div>
        {live.applied > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: T.utilGreenText }}>{live.applied.toLocaleString()}</div>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>applied</div>
          </div>
        )}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: T.utilGreenText }}>{sessionAccepted.toLocaleString()}</div>
          <div style={{ fontSize: T.fsXxs, color: T.mid }}>accepted</div>
        </div>
        {sessionRejected > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: T.breakRed }}>{sessionRejected.toLocaleString()}</div>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>rejected</div>
          </div>
        )}
        {heldBreakdown ? (
          <div
            data-testid="reconcile-breakdown"
            style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
          >
            {[...heldBreakdown.excludedByField.entries()].map(([field, count]) => (
              <span
                key={field}
                data-testid={`reconcile-excluded-chip-${field}`}
                style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold,
                  padding: "3px 9px", borderRadius: 10,
                  background: T.warnAmberBg, color: T.amberDark,
                  whiteSpace: "nowrap",
                }}
              >
                {count} excluded ({field})
              </span>
            ))}
            {heldBreakdown.newLeaseCount > 0 && (
              <span
                data-testid="reconcile-new-leases-chip"
                style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold,
                  padding: "3px 9px", borderRadius: 10,
                  background: T.bluePale, color: T.blueDeep,
                  whiteSpace: "nowrap",
                }}
              >
                {heldBreakdown.newLeaseCount} new lease{heldBreakdown.newLeaseCount === 1 ? "" : "s"} pending
              </span>
            )}
            <span
              data-testid="reconcile-pending-chip"
              style={{
                fontSize: T.fsXs, fontWeight: T.fwSemibold,
                padding: "3px 9px", borderRadius: 10,
                background: T.bg, color: T.mid,
                border: `1px solid ${T.border}`,
                whiteSpace: "nowrap",
              }}
            >
              {sessionPending.toLocaleString()} still pending
            </span>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, color: T.mid }}>{sessionPending.toLocaleString()}</div>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>pending</div>
          </div>
        )}
        {!isReadOnly && sessionPending > 0 && (
          <>
            <button
              type="button"
              data-testid="bulk-session-use-incoming-btn"
              disabled={busy}
              onClick={() => setConfirmDialog({ type: "bulk_session_incoming" })}
              style={{
                padding: "4px 12px", borderRadius: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.successBorder}`, background: T.utilGreen, color: T.utilGreenText,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap",
              }}
            >
              Use all incoming ({sessionPending.toLocaleString()})
            </button>
            <button
              type="button"
              data-testid="bulk-session-keep-current-btn"
              disabled={busy}
              onClick={() => setConfirmDialog({ type: "bulk_session_current" })}
              style={{
                padding: "4px 12px", borderRadius: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.border}`, background: T.white, color: T.mid,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap",
              }}
            >
              Keep all current ({sessionPending.toLocaleString()})
            </button>
          </>
        )}
        <div style={{ marginLeft: "auto" }}>
          <span style={{
            fontSize: T.fsXs, fontWeight: T.fwBold, padding: "3px 9px", borderRadius: 10,
            background: T.bluePale, color: T.blueDeep,
          }}>
            {STATUS_LABELS[session?.status] || session?.status}
          </span>
        </div>
      </div>

      {/* ── Error banners ── */}
      {error && (
        <div style={{
          flexShrink: 0,
          padding: "10px 18px",
          background: T.utilRed, borderBottom: `1px solid ${T.redRing}`, color: T.errorText, fontSize: T.fsBase,
        }}>
          {error?.message || String(error)}
        </div>
      )}
      {bulkError && (
        <div style={{ flexShrink: 0, padding: "0 18px" }}>
          <ErrorBanner
            error={bulkError.error}
            severity={bulkError.severity}
            onDismiss={() => setBulkError(null)}
          />
        </div>
      )}
      {partialApplyBanner && (
        <div
          data-testid="partial-apply-banner"
          role="status"
          style={{
            flexShrink: 0,
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 18px",
            background: T.alertErrBg, borderBottom: `1px solid ${T.alertErrBorder}`,
            color: T.breakRed, fontSize: T.fsSm,
          }}
        >
          <span style={{ flex: 1 }}>
            <strong>Applied {partialApplyBanner.applied.toLocaleString()} · Skipped {partialApplyBanner.skipped.toLocaleString()}</strong>
            {"- constraint error on "}
            {(() => {
              const displayIds = skippedLeaseIds.length <= 3
                ? skippedLeaseIds
                : skippedLeaseIds.slice(0, 2);
              const moreCount = skippedLeaseIds.length > 3 ? skippedLeaseIds.length - 2 : 0;
              return (
                <>
                  {displayIds.map((lid, i) => (
                    <React.Fragment key={lid}>
                      {i > 0 && ", "}
                      <button
                        type="button"
                        data-testid={`skipped-lease-anchor-${lid}`}
                        onClick={() => handleScrollToLease(lid)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: T.breakRed, fontWeight: T.fwBold,
                          fontFamily: "monospace", fontSize: "inherit",
                          padding: 0, textDecoration: "underline",
                        }}
                      >
                        {lid}
                      </button>
                    </React.Fragment>
                  ))}
                  {moreCount > 0 && ` + ${moreCount} more`}
                </>
              );
            })()}
            {". Review the highlighted row"}
            {skippedLeaseIds.length !== 1 ? "s" : ""}
            {"."}
          </span>
          <button
            type="button"
            aria-label="Dismiss partial apply notice"
            onClick={() => { setPartialApplyBanner(null); setSkippedDetailsMap(new Map()); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.breakRed, fontSize: T.fsMd5, lineHeight: 1, padding: "0 2px",
            }}
          >
            ×
          </button>
        </div>
      )}
      {/* ── Bulk-decide confirmation banner ── */}
      {bulkConfirmation && (
        <div
          data-testid="bulk-confirmation-banner"
          role="status"
          style={{
            flexShrink: 0,
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 18px",
            background: T.acceptedRowBg,
            borderBottom: `1px solid ${T.successBorder}`,
            color: T.utilGreenText, fontSize: T.fsSm,
          }}
        >
          <span style={{ flex: 1 }}>
            {bulkConfirmation}
          </span>
          {(heldBreakdown?.excludedByField.size > 0 || heldBreakdown?.newLeaseCount > 0) && (
            <a
              href="#"
              data-testid="review-held-items-link"
              onClick={(e) => { e.preventDefault(); scrollToHeldItems(); }}
              style={{
                fontSize: T.fsXs, fontWeight: T.fwSemibold,
                color: T.utilGreenText, textDecoration: "underline",
                whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer",
              }}
            >
              Review {sessionPending.toLocaleString()} held item{sessionPending === 1 ? "" : "s"}
            </a>
          )}
          <button
            type="button"
            aria-label="Dismiss bulk action notice"
            onClick={() => setBulkConfirmation(null)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.utilGreenText, fontSize: T.fsMd5, lineHeight: 1, padding: "0 2px",
            }}
          >
            ×
          </button>
        </div>
      )}
      {resumedAt && (
        <div
          data-testid="resumed-banner"
          role="status"
          style={{
            flexShrink: 0,
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 18px",
            background: T.blueSoft, borderBottom: `1px solid ${T.bluePale}`,
            color: T.blueDeep, fontSize: T.fsSm,
          }}
        >
          <span style={{ flex: 1 }}>
            Session resumed - diffs recomputed
          </span>
          <button
            type="button"
            aria-label="Dismiss resumed notice"
            onClick={() => setResumedAt(null)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.blueDeep, fontSize: T.fsMd5, lineHeight: 1, padding: "0 2px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Filter row ── */}
      <div style={{
        flexShrink: 0,
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 18px",
        borderBottom: `1px solid ${T.border}`,
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold }}>Show:</span>
        {FILTER_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`filter-${o.value}`}
            onClick={() => setFilter((prev) => (o.value === "all" || prev === o.value) ? "all" : o.value)}
            style={{
              padding: "4px 12px", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold,
              border: `1.5px solid ${filter === o.value ? T.blue : T.border}`,
              background: filter === o.value ? T.blueSoft : T.white,
              color: filter === o.value ? T.blue : T.mid,
              cursor: "pointer",
            }}
          >
            {o.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="toggle-show-unchanged"
          onClick={() => setShowUnchanged((p) => !p)}
          style={{
            padding: "4px 12px", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold,
            border: `1.5px solid ${showUnchanged ? T.blue : T.border}`,
            background: showUnchanged ? T.blueSoft : T.white,
            color: showUnchanged ? T.blue : T.mid,
            cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          {showUnchanged ? "Hide unchanged" : "Show unchanged fields"}
        </button>
        <span style={{ marginLeft: "auto", fontSize: T.fsSm, color: T.collectionCount }}>
          Showing {diffsMap.size.toLocaleString()} of {pagination.total.toLocaleString()} diffs loaded
          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                ...OUTBOUND_LINK_STYLE,
                marginLeft: 8,
                cursor: loadingMore ? "not-allowed" : "pointer",
              }}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </span>
      </div>

      {/* ── Scrollable diffs area ── */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 18px" }}>
        {session?.provenanceFallback && (
          <div
            data-testid="provenance-fallback-warning"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px",
              marginBottom: 12,
              background: T.utilAmber,
              border: `1px solid ${T.yellowRing}`,
              borderRadius: 6,
              fontSize: T.fsXs, color: T.blockedText,
            }}
          >
            <span style={{ fontSize: T.fsBase }}>⚠</span>
            <span>Source attribution unavailable for this import - fields extracted successfully.</span>
          </div>
        )}
        {session?.seatAdvisory && (
          <div
            data-testid="seat-advisory-banner"
            role="note"
            style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              padding: "8px 12px",
              marginBottom: 12,
              background: T.bluePale,
              border: `1px solid ${T.blueSoft}`,
              borderRadius: 6,
              fontSize: T.fsXs, color: T.blueDeep,
            }}
          >
            <span style={{ fontSize: T.fsSm, lineHeight: "18px", flexShrink: 0 }}>ℹ</span>
            <span>This appears to be a seat/membership agreement. Area type is managed separately in location settings.</span>
          </div>
        )}
        {session?.sourceType === "pdf_abstraction" &&
          Array.isArray(session.documentRoles) && session.documentRoles.length > 0 && (
          <DocumentSourcesPanel documentRoles={session.documentRoles} />
        )}
        {session?.sourceType === "pdf_abstraction" && Array.isArray(session.extractedFields) && (
          <PdfExtractionPanel
            extractedFields={session.extractedFields}
            collapsible
            defaultOpen={false}
          />
        )}
        {grouped.length === 0 && !hasMore && (
          <div style={{ padding: 32, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
            No diffs match this filter.
          </div>
        )}
        {(() => {
          // Attach heldItemsRef to the first group that contains at least one
          // pending diff (the first "held" row). This gives scrollToHeldItems a
          // precise target instead of a generic top-of-list anchor.
          let heldRefSet = false;
          return grouped.map(({ leaseId, diffs }) => {
            const groupRows = isReadOnly ? [] : (stagedRowsMap.get(leaseId ?? null) ?? []);
            const isFirstHeld =
              !heldRefSet && diffs.some((d) => d.status === "pending");
            if (isFirstHeld) heldRefSet = true;
            return (
              <div
                key={leaseId}
                data-lease-group-id={leaseId}
                ref={isFirstHeld ? heldItemsRef : null}
                data-testid={isFirstHeld ? "held-items-anchor" : undefined}
              >
                <LeaseGroup
                  leaseId={leaseId}
                  diffs={diffs}
                  onDecide={handleDecide}
                  onAcceptAll={(ids) => handleBulk("accept_lease", ids)}
                  stagedRows={groupRows}
                  onDeleteRow={groupRows.length > 0 ? (rowId) => setConfirmDialog({ type: "delete_row", rowId, leaseId }) : null}
                  busy={busy || isReadOnly}
                  showUnchangedPage={showUnchanged}
                  skippedDetailsMap={skippedDetailsMap}
                  expandTrigger={expandTriggers[leaseId] || 0}
                />
              </div>
            );
          });
        })()}
        {(
          <NewLeasesSection
            newLeaseDiffs={newLeaseDiffs}
            onDecide={handleDecide}
            busy={busy || isReadOnly}
            skippedDetailsMap={skippedDetailsMap}
            containerRef={newLeasesSectionRef}
          />
        )}
        {hasMore && (
          <div
            data-testid="load-more-callout"
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12,
              margin: "8px 0 4px",
              padding: "12px 16px",
              borderRadius: 8,
              background: T.blueSoft,
              border: `1.5px solid ${T.bluePale}`,
              color: T.blueDeep,
              fontSize: T.fsSm,
            }}
          >
            <span>
              {bulkDecideDone
                ? `${(pagination.total - diffsMap.size).toLocaleString()} more diff${pagination.total - diffsMap.size === 1 ? "" : "s"} already decided - load to view`
                : `${(pagination.total - diffsMap.size).toLocaleString()} more diff${pagination.total - diffsMap.size === 1 ? "" : "s"} not yet loaded`
              }
            </span>
            <button
              type="button"
              data-testid="load-more-callout-btn"
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                ...OUTBOUND_LINK_STYLE,
                padding: "5px 14px", borderRadius: 6,
                cursor: loadingMore ? "not-allowed" : "pointer",
                opacity: loadingMore ? 0.6 : 1, flexShrink: 0,
              }}
            >
              {loadingMore ? "Loading…" : "Load more diffs"}
            </button>
          </div>
        )}
      </div>

      {/* ── Sticky footer action bar ── */}
      {!isReadOnly && (
        <div style={{
          flexShrink: 0,
          background: T.white,
          borderTop: `1px solid ${T.border}`,
          boxShadow: T.shadowUpTray,
          borderRadius: "0 0 14px 14px",
        }}>
          {dirtyState && (
            <div
              data-testid="dirty-state-banner"
              style={{
                padding: "8px 18px",
                background: T.warnAmberBg, borderBottom: `1px solid ${T.yellowRing}`,
                color: T.blockedText, fontSize: T.fsSm, fontWeight: T.fwSemibold,
                display: "flex", alignItems: "center", gap: 10,
              }}
            >
              <span style={{ flex: 1 }}>
                The last bulk action failed and the diff list could not be refreshed - the displayed states may not match the server. Reload before applying.
              </span>
              <button
                type="button"
                data-testid="reload-session-btn"
                disabled={busy}
                onClick={handleReload}
                style={{
                  padding: "4px 12px", borderRadius: 5, fontSize: T.fsSm, fontWeight: T.fwSemibold,
                  border: `1.5px solid ${T.amber}`, background: T.utilAmber, color: T.blockedText,
                  cursor: busy ? "not-allowed" : "pointer", flexShrink: 0,
                }}
              >
                {busy ? "Reloading…" : "Reload"}
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px" }}>
            <span style={{ flex: 1, fontSize: T.fsSm, color: T.mid }}>
              {dirtyState
                ? "Reload to confirm diff states before applying."
                : acceptedCount > 0
                  ? `${acceptedCount.toLocaleString()} selection${acceptedCount === 1 ? "" : "s"} accepted - ready to apply${sessionPending > 0 ? ` · ${sessionPending.toLocaleString()} pending will remain unreviewed` : ""}`
                  : "Accept diffs above, then click Apply"}
            </span>
            <button
              type="button"
              data-testid="reject-session-btn"
              disabled={busy}
              onClick={() => setConfirmDialog({ type: "reject" })}
              style={{
                padding: "7px 16px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: `1.5px solid ${T.redRing}`, background: T.utilRed, color: T.breakRed,
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              Reject session
            </button>
            <button
              type="button"
              data-testid="apply-session-btn"
              disabled={busy || acceptedCount === 0 || dirtyState}
              title={dirtyState ? "Diff states may be out of sync with the server - reload first" : undefined}
              onClick={() => setConfirmDialog({ type: "apply" })}
              style={{
                padding: "7px 18px", borderRadius: 6, fontSize: T.fsBase, fontWeight: T.fwSemibold,
                border: "none",
                background: (busy || acceptedCount === 0 || dirtyState) ? T.border : T.blue,
                color: (busy || acceptedCount === 0 || dirtyState) ? T.mid : T.white,
                cursor: (busy || acceptedCount === 0 || dirtyState) ? "not-allowed" : "pointer",
              }}
            >
              {busy ? "Working…" : `Apply ${acceptedCount.toLocaleString()} accepted`}
            </button>
          </div>
        </div>
      )}

      {/* ── Confirm dialogs ── */}
      <ConfirmDialog
        isOpen={confirmDialog?.type === "apply"}
        title="Apply accepted changes and Adds?"
        message={
          sessionPending > 0
            ? `This will apply ${acceptedCount.toLocaleString()} accepted change${acceptedCount === 1 ? "" : "s"} or Add${acceptedCount === 1 ? "" : "s"} to leases. ${sessionPending.toLocaleString()} diff${sessionPending === 1 ? " has" : "s have"} not been reviewed and will remain pending. This cannot be undone.`
            : `This will apply ${acceptedCount.toLocaleString()} accepted change${acceptedCount === 1 ? "" : "s"} or Add${acceptedCount === 1 ? "" : "s"} to leases. This cannot be undone.`
        }
        confirmLabel="Apply"
        danger={false}
        onConfirm={handleApply}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        isOpen={confirmDialog?.type === "reject"}
        title="Reject entire session?"
        message="All pending diffs in this session will be rejected. No changes will be written to leases. This cannot be undone."
        confirmLabel="Reject session"
        danger
        onConfirm={handleReject}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        isOpen={confirmDialog?.type === "delete_row"}
        title="Delete staged row?"
        message={`This will permanently remove the staged row${confirmDialog?.leaseId ? ` for lease "${confirmDialog.leaseId}"` : ""} from this import session. The cached diff will be invalidated. This cannot be undone.`}
        confirmLabel="Delete row"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        isOpen={confirmDialog?.type === "bulk_session_incoming"}
        title="Use all incoming?"
        message={`Set ${sessionPending.toLocaleString()} pending diff${sessionPending === 1 ? "" : "s"} to use incoming? This will mark every pending diff as accepted.`}
        confirmLabel="Use all incoming"
        danger={false}
        onConfirm={() => handleBulkSession("accepted")}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        isOpen={confirmDialog?.type === "bulk_session_current"}
        title="Keep all current?"
        message={`Set ${sessionPending.toLocaleString()} pending diff${sessionPending === 1 ? "" : "s"} to keep current? This will reject every pending diff.`}
        confirmLabel="Keep all current"
        danger={false}
        onConfirm={() => handleBulkSession("rejected")}
        onCancel={() => setConfirmDialog(null)}
      />

    </>
  );
}
